import type { ReactNode } from "react";
import "./ExplorerWizard.css";

export type ExplorerWizardShellProps = {
  progressLabel: string;
  step?: number;
  totalSteps?: number;
  stepNumbers?: boolean;
  children: ReactNode;
  error?: string | null;
  onBack?: () => void;
  showBack?: boolean;
  onNext?: () => void;
  nextFormId?: string;
  nextLabel?: string;
  nextDisabled?: boolean;
  loading?: boolean;
  showFooter?: boolean;
  /** "below" = Back/Next under the white card (checkout reference). */
  footerPlacement?: "inside" | "below";
  /** Payment/membership step — centered card, mockup #2 header & buttons. */
  checkoutDesign?: boolean;
  /** Show Connect logo in the progress header (left-aligned). */
  showHeaderLogo?: boolean;
};

export default function ExplorerWizardShell({
  progressLabel: _progressLabel,
  step: _step,
  totalSteps: _totalSteps,
  stepNumbers: _stepNumbers = true,
  children,
  error,
  onBack,
  showBack = true,
  onNext,
  nextFormId,
  nextLabel = "Next",
  nextDisabled = false,
  loading = false,
  showFooter = true,
  footerPlacement = "inside",
  checkoutDesign = false,
  showHeaderLogo = true,
}: ExplorerWizardShellProps) {
  const footerContent = showFooter ? (
    <>
      {showBack && onBack ? (
        <button
          type="button"
          className="btn explorer-wizard-btn explorer-wizard-btn--ghost"
          onClick={onBack}
          disabled={loading}
        >
          Back
        </button>
      ) : null}
      {onNext || nextFormId ? (
        <button
          type={nextFormId ? "submit" : "button"}
          form={nextFormId}
          className="btn explorer-wizard-btn explorer-wizard-btn--primary"
          onClick={nextFormId ? undefined : onNext}
          disabled={loading || nextDisabled}
        >
          {loading ? "Please wait…" : nextLabel}
        </button>
      ) : null}
    </>
  ) : null;

  const rootClass = [
    "explorer-wizard-root",
    "position-relative min-vh-100 d-flex justify-content-center py-4 px-3 pb-5",
    checkoutDesign
      ? "explorer-wizard-root--checkout align-items-center"
      : "align-items-start",
  ]
    .filter(Boolean)
    .join(" ");

  const stackClass = [
    "position-relative z-1 w-100 mx-auto",
    checkoutDesign ? "explorer-wizard-stack--checkout" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const cardClass = [
    "explorer-wizard-card w-100 bg-white rounded-3 overflow-hidden",
    checkoutDesign ? "explorer-wizard-card--checkout" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const progressClass = [
    "explorer-wizard-progress",
    "d-flex align-items-center",
    checkoutDesign ? "explorer-wizard-progress--checkout" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={rootClass}>
      <div className="explorer-wizard-backdrop" aria-hidden />
      <div className={stackClass}>
        <div className={cardClass}>
          {showHeaderLogo ? (
            <div className={progressClass}>
              <img
                src="/connect-logo.png"
                alt="SMBF logo"
                className="explorer-wizard-progress__logo d-block"
              />
            </div>
          ) : null}
          <div
            className={[
              "explorer-wizard-body",
              checkoutDesign ? "" : "p-4 p-sm-4",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {error ? (
              <div className="alert alert-danger py-2 small mb-3" role="alert">
                {error}
              </div>
            ) : null}
            {children}
          </div>
          {showFooter && footerPlacement === "inside" ? (
              <div
                className={[
                  "explorer-wizard-footer",
                  checkoutDesign ? "explorer-wizard-footer--checkout" : "",
                  "d-flex flex-wrap justify-content-end align-items-center gap-2",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {footerContent}
              </div>
          ) : null}
        </div>
        {showFooter && footerPlacement === "below" ? (
          <div className="d-flex flex-wrap justify-content-end align-items-center gap-3 mt-3 px-1">
            {footerContent}
          </div>
        ) : null}
      </div>
    </div>
  );
}
